import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppStore } from '../../stores/appStore';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '../../components/ui/form';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { AlertCircle, ArrowLeft, Plus, X, Loader2, Copy, Link as LinkIcon } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { toast } from '../../components/ui/toast';
import { getOAuth2AuthorizeUrl, getOAuth2TokenUrl } from '../../lib/utils';

const editAppSchema = z.object({
  name: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(100, 'Name must be 100 characters or less'),
  redirectUris: z.array(z.string().url('Must be a valid URL')).min(1, 'At least one redirect URI is required'),
  appUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logoUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  logUrl: z.string().url('Must be a valid URL').optional().or(z.literal('')),
  scope: z.array(z.string()).optional(),
  isActive: z.enum(['true', 'false']),
});

export function EditApplication() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentApp, loading, error, fetchApplication, updateApp, clearError, setCurrentApp } = useAppStore();

  // Get orgId from navigation state
  const orgId = (location.state as any)?.orgId;
  const [scopesInput, setScopesInput] = useState('');

  const form = useForm<z.infer<typeof editAppSchema>>({
    resolver: zodResolver(editAppSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      redirectUris: [''],
      appUrl: '',
      logoUrl: '',
      logUrl: '',
      scope: [],
      isActive: 'true',
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'redirectUris' as never,
  });

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    clearError();

    // Check if we have orgId
    if (!orgId) {
      return;
    }

    // Fetch the application
    if (id && orgId) {
      fetchApplication(orgId, id).then((app) => {
        // Pre-populate form
        form.reset({
          name: app.name,
          redirectUris: app.redirect_uris.length > 0 ? app.redirect_uris : [''],
          appUrl: app.app_url || '',
          logoUrl: app.logo_url || '',
          logUrl: app.log_url || '',
          scope: app.scope || [],
          isActive: app.is_active ? 'true' : 'false',
        });
        // Set scopes input
        if (app.scope && app.scope.length > 0) {
          setScopesInput(app.scope.join(', '));
        }
      }).catch((error) => {
        // Error is already handled in the store and displayed via error state
        console.error('Failed to fetch application:', error);
      });
    }
  }, [id, orgId, fetchApplication, clearError, form]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setCurrentApp(null);
    };
  }, [setCurrentApp]);

  const handleScopesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScopesInput(e.target.value);
    const scopes = e.target.value.split(',').map(s => s.trim()).filter(s => s);
    form.setValue('scope', scopes);
  };

  const onSubmit = async (data: any) => {
    if (!id || !orgId) return;

    try {
      await updateApp(orgId, id, {
        name: data.name,
        redirectUris: data.redirectUris,
        appUrl: data.appUrl || undefined,
        logoUrl: data.logoUrl || undefined,
        logUrl: data.logUrl || undefined,
        scope: data.scope,
        isActive: data.isActive === true || data.isActive === 'true',
      });
      toast({
        title: 'Application Updated',
        description: `${data.name} has been successfully updated.`,
      });
      navigate('/dashboard/apps');
    } catch (error: any) {
      // Error already set in store and shown via Alert, also show toast
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update application',
        variant: 'destructive'
      });
    }
  };

  // Show loading state while fetching
  if (loading && !currentApp) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size={48} />
      </div>
    );
  }

  // Show error if no orgId available
  if (!orgId) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="mb-4">
          <Link to="/dashboard/apps">
            <Button variant="ghost" className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Applications
            </Button>
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Edit Application</h1>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Update OAuth2 application settings</p>
              </div>
            </div>
            <ThemeToggleCompact />
          </div>
        </div>

        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Missing Organization Context</AlertTitle>
          <AlertDescription>
            Cannot edit application without organization context. Please navigate from the application list.
          </AlertDescription>
        </Alert>

        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              To edit an application, please go back to the application list and click the Edit button.
            </p>
            <Link to="/dashboard/apps">
              <Button>Go to Application List</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-4">
        <Link to="/dashboard/apps">
          <Button variant="ghost" className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Applications
          </Button>
        </Link>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Edit Application</h1>
              <p className="text-gray-600 text-sm mt-0.5">Update OAuth2 application settings</p>
            </div>
          </div>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Update Failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Application Configuration</CardTitle>
          <CardDescription>
            Update your OAuth2 application settings
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="My App" {...field} />
                    </FormControl>
                    <FormDescription>
                      The display name for this application
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Redirect URIs *</FormLabel>
                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <FormField
                      control={form.control}
                      name={`redirectUris.${index}`}
                      render={({ field }) => (
                        <FormItem className="flex-1">
                          <FormControl>
                            <Input placeholder="https://example.com/callback" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      onClick={() => remove(index)}
                      disabled={fields.length === 1}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append('')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add URI
                </Button>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  OAuth2 will redirect to these URIs after authentication
                </p>
              </div>

              <FormField
                control={form.control}
                name="appUrl"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application URL (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="https://myapp.com" {...field} />
                    </FormControl>
                    <FormDescription>
                      Homepage URL for this application
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="logoUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Logo URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://myapp.com/logo.png" {...field} />
                      </FormControl>
                      <FormDescription>
                        URL to the application logo
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="logUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Log URL (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="https://myapp.com/logs" {...field} />
                      </FormControl>
                      <FormDescription>
                        Webhook URL for application logs
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel>Scopes (Optional)</FormLabel>
                <Input
                  placeholder="read, write, admin"
                  value={scopesInput}
                  onChange={handleScopesChange}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Comma-separated list of scopes (e.g., read, write, admin)
                </p>
              </div>

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Application Status</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        value={field.value as string}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </FormControl>
                    <FormDescription>
                      Set to Inactive to disable this application
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex gap-2 pt-2">
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {loading ? 'Updating...' : 'Update Application'}
                </Button>
                <Link to="/dashboard/apps">
                  <Button variant="outline" type="button">
                    Cancel
                  </Button>
                </Link>
              </div>

              {currentApp && (
                <div className="border-t pt-4 mt-4 space-y-3">
                  <h3 className="font-medium text-sm text-gray-900 dark:text-gray-100">OAuth2 Endpoints</h3>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-xs flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" />
                        Authorize URL:
                      </FormLabel>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const url = getOAuth2AuthorizeUrl(
                            currentApp.client_id,
                            currentApp.redirect_uris[0] || '',
                            currentApp.scope || undefined
                          );
                          navigator.clipboard.writeText(url);
                          toast({
                            title: 'Copied!',
                            description: 'Authorize URL copied to clipboard',
                          });
                        }}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <code className="block bg-gray-50 dark:bg-gray-900 px-2 py-1.5 rounded text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                      {getOAuth2AuthorizeUrl(
                        currentApp.client_id,
                        currentApp.redirect_uris[0] || '',
                        currentApp.scope || undefined
                      )}
                    </code>
                  </div>

                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <FormLabel className="text-xs flex items-center gap-1">
                        <LinkIcon className="h-3 w-3" />
                        Token URL:
                      </FormLabel>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(getOAuth2TokenUrl());
                          toast({
                            title: 'Copied!',
                            description: 'Token URL copied to clipboard',
                          });
                        }}
                      >
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <code className="block bg-gray-50 dark:bg-gray-900 px-2 py-1.5 rounded text-xs font-mono text-gray-700 dark:text-gray-300 break-all">
                      {getOAuth2TokenUrl()}
                    </code>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
